import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import usePageTitle from "../hooks/usePageTitle";
import "./SalesPages.css";
import "./Dashboard.css";

function MiniLineChart({ data = [], valueSuffix = "" }) {
  const width = 520;
  const height = 220;
  const padding = 28;

  const normalized = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const counts = data.map((item) => Number(item.count || 0));
    const max = Math.max(...counts, 1);
    const min = Math.min(...counts, 0);
    const range = Math.max(max - min, 1);

    return data.map((point, index) => {
      const x =
        padding +
        (index * (width - padding * 2)) /
          Math.max(data.length - 1, 1);

      const y =
        height -
        padding -
        ((Number(point.count || 0) - min) / range) *
          (height - padding * 2);

      return {
        ...point,
        x,
        y,
        count: Number(point.count || 0)
      };
    });
  }, [data]);

  const pathD = useMemo(() => {
    if (!normalized.length) return "";

    return normalized
      .map(
        (point, index) =>
          `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`
      )
      .join(" ");
  }, [normalized]);

  if (!data.length) {
    return (
      <div className="dashboard-chart-empty">
        No trend data available.
      </div>
    );
  }

  return (
    <div className="dashboard-chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="dashboard-line-chart"
        role="img"
        aria-label="Trend chart"
      >
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          className="dashboard-chart-axis"
        />

        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          className="dashboard-chart-axis"
        />

        <path
          d={pathD}
          className="dashboard-chart-line"
        />

        {normalized.map((point, index) => (
          <g key={`${point.label}-${index}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              className="dashboard-chart-dot"
            />

            <text
              x={point.x}
              y={point.y - 10}
              textAnchor="middle"
              className="dashboard-chart-value"
            >
              {point.count}
              {valueSuffix}
            </text>

            <text
              x={point.x}
              y={height - 8}
              textAnchor="middle"
              className="dashboard-chart-label"
            >
              {point.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function AdminDashboard() {
  usePageTitle("Admin Dashboard");

  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const getMetricValue = (...keys) => {
    for (const key of keys) {
      const value = summary?.metrics?.[key];

      if (
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return value;
      }
    }

    return 0;
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);

    return numericValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatSubscriptionStatus = (value) => {
    if (!value) return "";

    return String(value)
      .split("_")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1)
      )
      .join(" ");
  };

  const metricMeta = [
    {
      key: "activeSubscribers",
      title: "Active Subscribers",
      description: "Active and cancel-pending accounts",
      type: "number"
    },
    {
      key: "trialUsers",
      title: "Trial Users",
      description: "Subscribers currently in trial",
      type: "number"
    },
    {
      key: "subscriptionUsers",
      title: "Subscription Users",
      description: "Users on paid subscriptions",
      type: "number"
    },
    {
      key: "trialConversionRate",
      title: "Trial Conversion",
      description: "Trials converted to subscriptions",
      type: "percent"
    },
    {
      key: "cancellations",
      aliases: [
        "cancellationCount",
        "cancelledSubscribers"
      ],
      title: "Cancellations",
      description: "Scheduled and completed cancellations",
      type: "number"
    },
    {
      key: "mrrAtRisk",
      aliases: [
        "cancellationMrrAtRisk",
        "cancellationMRRAtRisk"
      ],
      title: "MRR at Risk",
      description: "Revenue tied to pending cancellations",
      type: "currency"
    },
    {
      key: "monthlyRecurringRevenue",
      aliases: ["mrr"],
      title: "Monthly Recurring Revenue",
      description: "Current recurring monthly revenue",
      type: "currency"
    },
    {
      key: "paymentsCollectedThisMonth",
      aliases: [
        "monthlyPaymentsCollected",
        "paymentsCollected"
      ],
      title: "Payments Collected This Month",
      description: "Successful invoice payments this month",
      type: "currency"
    }
  ];

  useEffect(() => {
    const loadDashboard = async () => {
      const token = localStorage.getItem("token");

      try {
        setLoading(true);

        const res = await fetch(
          `${API_BASE_URL}/api/admin-dashboard/summary`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = res.ok ? await res.json() : null;

        setSummary(
          data && typeof data === "object" ? data : null
        );
      } catch (err) {
        console.error(
          "Admin dashboard load failed:",
          err
        );

        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const renderMetricValue = (item) => {
    const rawValue = getMetricValue(
      item.key,
      ...(item.aliases || [])
    );

    if (item.type === "currency") {
      return formatCurrency(rawValue);
    }

    if (item.type === "percent") {
      return `${Number(rawValue || 0).toFixed(1)}%`;
    }

    return Number(rawValue || 0).toLocaleString("en-US");
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page dashboard-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">
              Agent Book CRM
            </p>

            <h1 className="sales-title">
              Admin Dashboard
            </h1>

            <p className="sales-subtitle">
              Platform growth, retention, subscription,
              cancellation, and revenue snapshot.
            </p>
          </div>
        </div>

        <div className="dashboard-kpi-grid">
          {metricMeta.map((item) => (
            <div
              key={item.key}
              className="dashboard-kpi-card sales-section-card"
            >
              <div className="dashboard-kpi-head">
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>

              <div className="dashboard-kpi-value">
                {loading ? "—" : renderMetricValue(item)}
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-summary-grid dashboard-summary-grid-two-wide">
          <div className="sales-table-card dashboard-summary-card dashboard-chart-card">
            <div className="sales-table-header">
              <div>
                <h2>Subscriber Trend</h2>
                <p>
                  Active subscribers over the last 6
                  months.
                </p>
              </div>
            </div>

            <div className="dashboard-summary-body">
              <MiniLineChart
                data={summary?.subscriberTrend || []}
              />
            </div>
          </div>

          <div className="sales-table-card dashboard-summary-card dashboard-chart-card">
            <div className="sales-table-header">
              <div>
                <h2>Subscription User Trend</h2>
                <p>
                  Total subscription users over the last
                  6 months.
                </p>
              </div>
            </div>

            <div className="dashboard-summary-body">
              <MiniLineChart
                data={
                  summary?.subscriptionUserTrend || []
                }
              />
            </div>
          </div>
        </div>

        <div className="dashboard-summary-grid dashboard-summary-grid-three">
          <div className="sales-table-card dashboard-summary-card">
            <div className="sales-table-header">
              <div>
                <h2>Retention Snapshot</h2>
                <p>
                  Subscriber retention based on
                  age-qualified cohorts.
                </p>
              </div>
            </div>

            <div className="dashboard-summary-body">
              <div className="dashboard-funnel-row">
                <span>3-Month Retention</span>
                <strong>
                  {Number(
                    summary?.retention?.threeMonth || 0
                  ).toFixed(1)}
                  %
                </strong>
              </div>

              <div className="dashboard-funnel-row">
                <span>6-Month Retention</span>
                <strong>
                  {Number(
                    summary?.retention?.sixMonth || 0
                  ).toFixed(1)}
                  %
                </strong>
              </div>

              <div className="dashboard-funnel-row">
                <span>1-Year Retention</span>
                <strong>
                  {Number(
                    summary?.retention?.oneYear || 0
                  ).toFixed(1)}
                  %
                </strong>
              </div>
            </div>
          </div>

          <div className="sales-table-card dashboard-summary-card">
            <div className="sales-table-header">
              <div>
                <h2>Plan Distribution</h2>
                <p>
                  How subscribers are distributed across
                  plans.
                </p>
              </div>
            </div>

            <div className="dashboard-summary-body">
              {summary?.planDistribution?.length ? (
                summary.planDistribution.map(
                  (plan, index) => (
                    <div
                      key={`${plan.planName}-${index}`}
                      className="dashboard-funnel-row"
                    >
                      <span>{plan.planName}</span>
                      <strong>{plan.count}</strong>
                    </div>
                  )
                )
              ) : (
                <div className="sales-empty-cell dashboard-empty">
                  No plan data
                </div>
              )}
            </div>
          </div>

          <div className="sales-table-card dashboard-summary-card">
            <div className="sales-table-header">
              <div>
                <h2>Revenue Snapshot</h2>
                <p>
                  Current recurring revenue and
                  cancellation exposure.
                </p>
              </div>
            </div>

            <div className="dashboard-summary-body">
              <div className="dashboard-funnel-row">
                <span>Monthly Recurring Revenue</span>
                <strong>
                  {formatCurrency(
                    getMetricValue(
                      "monthlyRecurringRevenue",
                      "mrr"
                    )
                  )}
                </strong>
              </div>

              <div className="dashboard-funnel-row">
                <span>Payments This Month</span>
                <strong>
                  {formatCurrency(
                    getMetricValue(
                      "paymentsCollectedThisMonth",
                      "monthlyPaymentsCollected",
                      "paymentsCollected"
                    )
                  )}
                </strong>
              </div>

              <div className="dashboard-funnel-row">
                <span>MRR at Risk</span>
                <strong>
                  {formatCurrency(
                    getMetricValue(
                      "mrrAtRisk",
                      "cancellationMrrAtRisk",
                      "cancellationMRRAtRisk"
                    )
                  )}
                </strong>
              </div>

              <div className="dashboard-funnel-row">
                <span>Cancellations</span>
                <strong>
                  {Number(
                    getMetricValue(
                      "cancellations",
                      "cancellationCount",
                      "cancelledSubscribers"
                    )
                  ).toLocaleString("en-US")}
                </strong>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-lists-grid dashboard-lists-grid-admin">
          <div className="sales-table-card dashboard-list-card">
            <div className="sales-table-header">
              <div>
                <h2>Recent Subscribers</h2>
                <p>
                  {summary?.recentSubscribers?.length ??
                    0}{" "}
                  recent
                </p>
              </div>
            </div>

            <div className="dashboard-list-body">
              {summary?.recentSubscribers?.length ? (
                summary.recentSubscribers.map(
                  (subscriber) => (
                    <button
                      key={subscriber.id}
                      type="button"
                      className="dashboard-activity-row"
                      onClick={() =>
                        navigate(
                          `/subscriber-profile/${subscriber.id}`
                        )
                      }
                    >
                      <div className="dashboard-activity-title">
                        {subscriber.business_name ||
                          "Unnamed Subscriber"}
                      </div>

                      <div className="dashboard-activity-meta">
                        {subscriber.primary_contact_name ||
                          "No Contact"}

                        {subscriber.planName
                          ? ` · ${subscriber.planName}`
                          : ""}

                        {subscriber.subscription_status
                          ? ` · ${formatSubscriptionStatus(
                              subscriber.subscription_status
                            )}`
                          : ""}
                      </div>
                    </button>
                  )
                )
              ) : (
                <div className="sales-empty-cell dashboard-empty">
                  No recent subscribers
                </div>
              )}
            </div>
          </div>

          <div className="sales-table-card dashboard-list-card">
            <div className="sales-table-header">
              <div>
                <h2>Recent Sales Leads</h2>
                <p>
                  {summary?.recentSalesLeads?.length ??
                    0}{" "}
                  recent
                </p>
              </div>
            </div>

            <div className="dashboard-list-body">
              {summary?.recentSalesLeads?.length ? (
                summary.recentSalesLeads.map(
                  (lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      className="dashboard-activity-row"
                      onClick={() =>
                        navigate("/sales-leads")
                      }
                    >
                      <div className="dashboard-activity-title">
                        {[lead.first_name, lead.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                          "Unnamed Sales Lead"}
                      </div>

                      <div className="dashboard-activity-meta">
                        {lead.source || "No Source"}

                        {lead.status
                          ? ` · ${lead.status}`
                          : ""}
                      </div>
                    </button>
                  )
                )
              ) : (
                <div className="sales-empty-cell dashboard-empty">
                  No recent sales leads
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;